import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CurrencyPairManagementPage.css";

const mapPairFromApi = (p) => ({
  id: p._id,
  pairName: `${p.base_currency || ""}/${p.quote_currency || ""}`,
  sellPrice: p.sell_price ?? "",
  buyPrice: p.buy_price ?? "",
  availability: p.available || "GLOBAL",
  status: p.status || "Active",
});

const CurrencyPairManagementPage = () => {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchPair, setSearchPair] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);
  const [listModal, setListModal] = useState(null);
  const [statusActionLoading, setStatusActionLoading] = useState(false);

  const fetchPairs = useCallback(async () => {
    setLoading(true);
    setListError("");
    const params = statusFilter ? { status: statusFilter } : {};
    const res = await AdminService.getPairsList(params);
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setPairs(res.data.map(mapPairFromApi));
    } else {
      setListError(res?.message || "Failed to load pairs.");
      setPairs([]);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPairs();
  }, [fetchPairs]);

  const filteredPairs = pairs.filter((p) =>
    p.pairName.toLowerCase().includes(searchPair.toLowerCase())
  );
  const totalRows = filteredPairs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEnd = Math.min(currentPage * rowsPerPage, totalRows);
  const paginatedPairs = filteredPairs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleToggleStatus = async (pairId) => {
    const pair = pairs.find((p) => p.id === pairId);
    if (!pair) return;
    const newStatus = pair.status === "Active" ? "Inactive" : "Active";
    setStatusActionLoading(true);
    const res = await AdminService.updatePairStatus(pairId, newStatus);
    setStatusActionLoading(false);
    if (res?.success) {
      setPairs((prev) =>
        prev.map((p) => (p.id === pairId ? { ...p, status: newStatus } : p))
      );
      setListModal(null);
    } else {
      setListError(res?.message || "Failed to update pair status.");
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage currency-pair-page">
      <section className="dashboard-hero currency-pair-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Currency Pair Management</span>
          </p>
          <h1 className="dashboard-hero__title">Currency Pair Management</h1>
          <p className="dashboard-hero__subtitle">View and manage trading pairs.</p>
        </div>
        <div className="currency-pair-hero__actions">
          <Link to="/dashboard/addCurrencyPair" className="cp-btn cp-btn--primary cp-btn--add">
            <i className="fas fa-plus" /> Add Currency Pair
          </Link>
        </div>
        <div className="dashboard-hero__icon currency-pair-hero__icon">
          <i className="fas fa-columns" />
        </div>
      </section>

      <div className="dashboard-body currency-pair-body">
        <div className="currency-pair-card currency-pair-card--table-only">
          <div className="currency-pair-table-block">
            <div className="currency-pair-table-head">
              <div className="currency-pair-section-head">
                <i className="fas fa-list" />
                <h2>Currency Pair Details ({pairs.length})</h2>
              </div>
              <div className="cp-table-actions">
                <div className="cp-filter-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="cp-filter-select"
                  >
                    <option value="">All status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="cp-export-wrap">
                  <button type="button" className="cp-btn cp-btn--export" onClick={() => setExportOpen((v) => !v)}>
                    <i className="fas fa-download" /> Export <i className="fas fa-chevron-down" />
                  </button>
                  {exportOpen && (
                    <div className="cp-export-dropdown">
                      <button type="button">CSV</button>
                      <button type="button">Excel</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {listError && (
              <div className="cp-list-error">
                <i className="fas fa-exclamation-triangle" /> {listError}
                <button type="button" className="cp-btn cp-btn--outline cp-btn--sm" onClick={fetchPairs}>Retry</button>
              </div>
            )}

            {loading ? (
              <div className="cp-loading">
                <i className="fas fa-spinner fa-spin" /> Loading pairs…
              </div>
            ) : (
              <>
                <div className="cp-search-wrap">
                  <i className="fas fa-search" />
                  <input
                    type="text"
                    placeholder="Search here..."
                    value={searchPair}
                    onChange={(e) => setSearchPair(e.target.value)}
                    className="cp-search-input"
                  />
                </div>
                <div className="cp-table-wrap">
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Pair Name</th>
                        <th>Sell Price</th>
                        <th>Buy Price</th>
                        <th>Availability</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPairs.map((row) => (
                        <tr key={row.id}>
                          <td><strong>{row.pairName}</strong></td>
                          <td>{row.sellPrice}</td>
                          <td>{row.buyPrice}</td>
                          <td>
                            <span className={`cp-badge cp-badge--${row.availability === "GLOBAL" ? "global" : "local"}`}>
                              <i className="fas fa-globe" /> {row.availability}
                            </span>
                          </td>
                          <td>
                            <span className={`cp-badge cp-badge--${row.status === "Active" ? "global" : "local"}`}>
                              {row.status}
                            </span>
                          </td>
                          <td>
                            <div className="cp-action-btns">
                              <button
                                type="button"
                                className={`cp-action-btn cp-action-btn--list ${row.status === "Active" ? "" : "cp-action-btn--delist"}`}
                                onClick={() => setListModal(row.id)}
                                title={row.status === "Active" ? "Delist" : "List"}
                                disabled={statusActionLoading}
                              >
                                <i className={`fas ${row.status === "Active" ? "fa-times" : "fa-check"}`} />
                                {row.status === "Active" ? "Delist" : "List"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cp-pagination">
                  <div className="cp-pagination-rows">
                    <span>Rows per page:</span>
                    <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="cp-pagination-info">
                    {pageStart}-{pageEnd} of {totalRows}
                  </div>
                  <div className="cp-pagination-nav">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      className="cp-pagination-btn"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      className="cp-pagination-btn"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {listModal && (() => {
        const pair = pairs.find((p) => p.id === listModal);
        if (!pair) return null;
        const isCurrentlyActive = pair.status === "Active";
        return (
          <div className="cp-modal-overlay" onClick={() => setListModal(null)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{isCurrentlyActive ? "Delist Pair" : "List Pair"}</h3>
              <p>
                {isCurrentlyActive
                  ? `Delist ${pair.pairName}? The pair will be set to Inactive.`
                  : `List ${pair.pairName}? The pair will be set to Active.`}
              </p>
              <div className="cp-modal-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={() => setListModal(null)}>Cancel</button>
                <button
                  type="button"
                  className="cp-btn cp-btn--primary"
                  onClick={() => handleToggleStatus(listModal)}
                  disabled={statusActionLoading}
                >
                  {statusActionLoading ? <><i className="fas fa-spinner fa-spin" /> Updating…</> : <><i className="fas fa-check" /> Confirm</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </main>
  );
};

export default CurrencyPairManagementPage;
